import type { PermissionGrant } from "@/core/types/auth";
import type { Lead, LeadPriority, LeadStatus } from "@/core/types/lead";

export const demoTenant = {
  id: "11111111-1111-1111-1111-111111111111",
  code: "demo",
  name: "شرکت دمو CRM",
  status: "active",
  defaultCulture: "fa-IR",
  timeZoneId: "Asia/Tehran",
  defaultCurrency: "IRR",
};

export const demoTeam = {
  id: "66666666-6666-6666-6666-666666666666",
  name: "تیم فروش مرکزی",
};

export const demoUsers = {
  manager: {
    role: "manager" as const,
    userId: "22222222-2222-2222-2222-222222222222",
    membershipId: "33333333-3333-3333-3333-333333333333",
    displayName: "مدیر فروش دمو",
    username: "manager@demo.local",
    password: "Demo123!",
  },
  employee: {
    role: "employee" as const,
    userId: "77777777-7777-7777-7777-777777777777",
    membershipId: "88888888-8888-8888-8888-888888888888",
    displayName: "کارشناس فروش دمو",
    username: "employee@demo.local",
    password: "Demo123!",
  },
};

export const managerPermissions: PermissionGrant[] = [
  { code: "tenancy.read", scope: "tenant" },
  { code: "identity.me.read", scope: "tenant" },
  { code: "leads.read", scope: "tenant" },
  { code: "leads.create", scope: "tenant" },
  { code: "leads.update", scope: "tenant" },
  { code: "leads.assign", scope: "tenant" },
  { code: "leads.duplicate-check", scope: "tenant" },
  { code: "leads.qualify", scope: "tenant" },
  { code: "leads.disqualify", scope: "tenant" },
  { code: "leads.convert", scope: "tenant" },
  { code: "contacts.read", scope: "tenant" },
  { code: "contacts.create", scope: "tenant" },
  { code: "accounts.read", scope: "tenant" },
  { code: "accounts.create", scope: "tenant" },
  { code: "activities.read", scope: "tenant" },
  { code: "activities.create", scope: "tenant" },
  { code: "activities.complete", scope: "tenant" },
  { code: "opportunities.read", scope: "tenant" },
  { code: "opportunities.create", scope: "tenant" },
  { code: "campaigns.read", scope: "tenant" },
  { code: "campaigns.manage", scope: "tenant" },
  { code: "campaigns.approve", scope: "tenant" },
  { code: "campaigns.send", scope: "tenant" },
  { code: "campaigns.reports.read", scope: "tenant" },
];

export const employeePermissions: PermissionGrant[] = [
  { code: "tenancy.read", scope: "own" },
  { code: "identity.me.read", scope: "own" },
  { code: "leads.read", scope: "own" },
  { code: "leads.create", scope: "own" },
  { code: "leads.update", scope: "own" },
  { code: "leads.duplicate-check", scope: "own" },
  { code: "leads.qualify", scope: "own" },
  { code: "leads.disqualify", scope: "own" },
  { code: "leads.convert", scope: "own" },
  { code: "contacts.read", scope: "own" },
  { code: "contacts.create", scope: "own" },
  { code: "accounts.read", scope: "own" },
  { code: "accounts.create", scope: "own" },
  { code: "activities.read", scope: "own" },
  { code: "activities.create", scope: "own" },
  { code: "activities.complete", scope: "own" },
  { code: "opportunities.read", scope: "own" },
  { code: "opportunities.create", scope: "own" },
];

const firstNames = [
  "علی",
  "سارا",
  "محمد",
  "نگار",
  "رضا",
  "مریم",
  "امیر",
  "زهرا",
  "حامد",
  "الهام",
];
const lastNames = [
  "احمدی",
  "محمدی",
  "کریمی",
  "رضایی",
  "صادقی",
  "نوری",
  "قاسمی",
  "اکبری",
];
const companies = [
  "صنایع آریا",
  "پارس تجهیز",
  "راهکار نوین",
  "توسعه شرق",
  "بازرگانی سپهر",
  "فناوری فردا",
];
const sources = [
  "نمایشگاه خودرو",
  "وب‌سایت",
  "معرفی مشتری",
  "کمپین پیامکی",
  "تماس ورودی",
  "شبکه اجتماعی",
];
const statuses: LeadStatus[] = [
  "new",
  "working",
  "qualified",
  "converted",
  "disqualified",
];
const priorities: LeadPriority[] = ["low", "normal", "high", "urgent"];

const encodeVersion = (index: number): string => {
  const bytes = new Uint8Array(8);
  new DataView(bytes.buffer).setBigUint64(0, BigInt(index + 1));
  return btoa(String.fromCharCode(...bytes));
};

const leadId = (index: number): string =>
  `a${String(index + 1).padStart(7, "0")}-1111-4111-8111-${String(index + 1).padStart(12, "0")}`;

export const createSeedLeads = (): Lead[] => {
  const now = Date.now();

  return Array.from({ length: 56 }, (_, index) => {
    const status = statuses[index % statuses.length];
    const priority = priorities[(index * 3) % priorities.length];
    const employeeOwned = index % 3 !== 0;
    const unassigned = index % 11 === 0;
    const createdAt = new Date(now - index * 8.5 * 60 * 60 * 1000);
    const updatedAt =
      index % 4 === 0 ? null : new Date(createdAt.getTime() + 90 * 60 * 1000);
    const firstName = firstNames[index % firstNames.length];
    const lastName = lastNames[(index * 2) % lastNames.length];

    return {
      id: leadId(index),
      firstName,
      lastName,
      companyName: companies[index % companies.length],
      mobile: `+98912${String(1000000 + index * 371).slice(-7)}`,
      email: index % 4 === 0 ? null : `lead${index + 1}@example.com`,
      status,
      priority,
      score: Math.min(100, 25 + ((index * 13) % 76)),
      source: sources[index % sources.length],
      ownerMembershipId: unassigned
        ? null
        : employeeOwned
          ? demoUsers.employee.membershipId
          : demoUsers.manager.membershipId,
      ownerTeamId: unassigned ? null : demoTeam.id,
      branchId: null,
      chiefExecutiveName: `مهندس ${lastName}`,
      chiefExecutivePhone: `+98910${String(2000000 + index * 419).slice(-7)}`,
      industry: ["تولید", "فناوری اطلاعات", "بازرگانی", "خدمات سازمانی"][
        index % 4
      ],
      contactPersonName: index % 3 === 0 ? `خانم ${lastName}` : null,
      contactPersonPhone:
        index % 3 === 0
          ? `+98911${String(3000000 + index * 293).slice(-7)}`
          : null,
      description:
        priority === "urgent"
          ? "درخواست پیگیری فوری و دریافت پیشنهاد تجاری."
          : "سرنخ نمونه برای نمایش گردش کار داشبورد CRM.",
      createdAtUtc: createdAt.toISOString(),
      updatedAtUtc: updatedAt?.toISOString() ?? null,
      rowVersion: encodeVersion(index),
      duplicateCheck: null,
    };
  });
};
